import classNames from "classnames";
import { useFeature } from "flagged";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import PublishedWorkflowTemplate from "./PublishedWorkflowTemplate";
import EmptyStateIcon from "components/icons/EmptyState";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import MultiselectDropdown from "components/multiselect-dropdown/MultiselectDropdown";
import SearchView from "components/search/SearchView";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import workflowStyle from "components/workflows/all-workflows-table.scss";
import { compareEditorVersionDtos, mergeAndSortEditorVersions } from "components/workflows/WorkflowUtil";
import { AUTH_ASSET_INTEGRATION } from "domain/authority";
import {
    BDE_WORKFLOW_EDITOR_VERSION_7_12_0,
    BDE_WORKFLOW_EDITOR_VERSION_7_13_0,
    BMDE_WORKFLOW_EDITOR_VERSION_5_4_0,
    BMDE_WORKFLOW_EDITOR_VERSION_5_5_0,
} from "domain/globalConstants";
import { FeatureLicenseType } from "domain/users";
import { TemplateTableData } from "domain/workflowTemplates";
import { FLAG_ASSET_MANAGEMENT, FLAG_WORKFLOW_EDITOR_V3 } from "services/feature/FeatureFlagService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import {
    CORE_ACTIVITIES,
    FilterState,
    PROCESSOR,
    templateService,
    TemplateType,
    USE_CASES,
} from "services/workflows/TemplateService";
import {
    ManifestWorkflowEditor,
    Profile,
    profileToFeatureLicenseMap,
    profileToNameMap,
    WorkflowEditorV3SupportedEntry,
    WorkflowService,
} from "services/workflows/WorkflowService";
import { StoreState } from "store";
import { setUser } from "store/user";
import buttonStyle from "styles/buttons.scss";
import { default as form, default as formStyle } from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface CardState {
    workflowTemplatesData: TemplateTableData[];
    count: number;
    cursor: string;
    scrollPosition?: number;
}

const mapState = (state: StoreState) => ({
    user: state.userReducer.user,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
    theme: state.themeReducer.theme,
});

const connector = connect(mapState, { setUser });

const WorkflowTemplateCardView = (
    props: ConnectedProps<typeof connector> & {
        search: string;
        count: number;
        setCount: React.Dispatch<React.SetStateAction<number>>;
        workflowService: WorkflowService;
    }
): JSX.Element => {
    const { t } = useTranslation();
    const workflowEditorV3Flag = useFeature(FLAG_WORKFLOW_EDITOR_V3);
    const assetManagementFlag = useFeature(FLAG_ASSET_MANAGEMENT);
    const [workflowEditorV3Supported, setWorkflowEditorV3Supported] = React.useState<WorkflowEditorV3SupportedEntry[]>(
        []
    );
    const [workflowEditors, setWorkflowEditors] = React.useState<ManifestWorkflowEditor[]>([]);
    const [manifestLoaded, setManifestLoaded] = React.useState<boolean>(false);
    const [optionList, setOptionList] = React.useState<JSX.Element[] | undefined>(undefined);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [initialLoading, setInitialLoading] = React.useState<boolean>(false);
    const [search, setSearchQuery] = React.useState("");
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [filterProduct, setFilterProduct] = React.useState("");
    const [filterProductType, setFilterProductType] = React.useState("");
    const [filterTemplateType, setFilterTemplateType] = React.useState("");
    const [filterUseCases, setFilterUseCases] = React.useState<FilterState>({ list: [] });
    const [filterProcessor, setFilterProcessor] = React.useState<FilterState>({ list: [] });
    const [filterCoreActivities, setFilterCoreActivities] = React.useState<FilterState>({ list: [] });
    const [cardState, setCardState] = React.useState<CardState>({
        workflowTemplatesData: [],
        count: 0,
        cursor: "",
        scrollPosition: 0,
    });

    const versionValue = filterProductType.includes("Erasure")
        ? filterProductType.replace("Erasure", "")
        : filterProductType.replace("Diagnostics", "");

    const fetchData = (initialLoading: boolean, abortController: AbortController) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        templateService
            .fetchWorkflowTemplates(
                search,
                initialLoading ? "" : cardState.cursor,
                filterProduct.toUpperCase(),
                filterTemplateType,
                filterUseCases.list,
                filterProcessor.list,
                filterCoreActivities.list,
                true,
                versionValue.trimEnd(),
                "",
                "",
                false,
                "",
                false,
                abortController
            )
            .then((data) => {
                setCardState((prevState) => ({
                    ...prevState,
                    scrollPosition: prevState.workflowTemplatesData.length - 1,
                    workflowTemplatesData: prevState.workflowTemplatesData.concat(data.workflowTemplatesTableData),
                    count: data.count,
                    cursor: data.cursor,
                }));
                setLoading(false);
                setRequestFailureMessage("");
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("workflowTemplatesManager.template.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                }
            });
    };

    React.useEffect(() => {
        const abortController = new AbortController();
        abortControllers.push(abortController);

        props.workflowService
            .fetchWorkflowEditors(Profile.ALL, abortController)
            .then((workflowEditors) => {
                setWorkflowEditors(workflowEditors);
                setManifestLoaded(true);
            })
            .catch(() => logger.error("Failed to fetch workflow editors"))
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setManifestLoaded(true);
                }
            });

        if (workflowEditorV3Flag) {
            const v3Controller = new AbortController();
            abortControllers.push(v3Controller);
            props.workflowService
                .fetchWorkflowEditorV3Supported(v3Controller)
                .then(setWorkflowEditorV3Supported)
                .catch(() =>
                    logger.error("Failed to fetch V3 supported versions; V3 editor options will not be shown.")
                );
        }

        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [workflowEditorV3Flag]);

    React.useEffect(() => {
        let sortedEditors = workflowEditors
            .filter((each) => each.editorGeneration === "V2")
            .filter((editor) => {
                if ([Profile.BDE.toString(), Profile.LUN.toString(), Profile.BMP.toString()].includes(filterProduct)) {
                    return filterProduct === editor.profile;
                }
                return Profile.BMDE === editor.profile || Profile.BMDE_DIAGNOSTIC === editor.profile;
            })
            .sort(compareEditorVersionDtos);

        //  Remove the BDE new generation editor old versions from list which
        //  do not support workflow templates.
        if (filterProduct === Profile.BDE) {
            sortedEditors = sortedEditors.filter(
                (each) =>
                    !each.version.includes(BDE_WORKFLOW_EDITOR_VERSION_7_12_0) &&
                    !each.version.includes(BDE_WORKFLOW_EDITOR_VERSION_7_13_0)
            );
        }

        // Remove the BMDE new generation editor old versions from list which
        // do not support workflow templates.
        if (filterProduct === Profile.BMDE || filterProduct === Profile.BMDE_DIAGNOSTIC) {
            sortedEditors = sortedEditors.filter(
                (each) =>
                    !each.version.includes(BMDE_WORKFLOW_EDITOR_VERSION_5_4_0) &&
                    !each.version.includes(BMDE_WORKFLOW_EDITOR_VERSION_5_5_0)
            );
        }
        const removeDuplicateVersion = sortedEditors.filter(
            (obj, index, self) => index === self.findIndex((each) => each.version === obj.version)
        );

        const allVersions = mergeAndSortEditorVersions(
            removeDuplicateVersion.map((entry) => entry.version),
            workflowEditorV3Supported,
            filterProduct
        );
        setOptionList(allVersions.map((version) => <option key={version}>{version}</option>));
    }, [filterProduct, workflowEditorV3Supported]);

    React.useEffect(() => {
        const abortController = new AbortController();
        setCardState({ workflowTemplatesData: [], count: 0, cursor: "", scrollPosition: 0 });
        fetchData(true, abortController);
        return () => {
            abortController.abort();
        };
    }, [
        props.count,
        search,
        filterProduct.toUpperCase(),
        filterProductType,
        filterTemplateType,
        filterUseCases,
        filterProcessor,
        filterCoreActivities,
    ]);

    return (
        <>
            <div className={workflowStyle.titleContainer}>{t("workflowTemplate.title")}</div>
            <div className={layoutStyle.aboveTable}>
                <div className={classNames(workflowStyle.filter, workflowStyle.filterLongContent)}>
                    <div className={formStyle.search}>
                        <SearchView
                            setSearch={setSearchQuery}
                            searchInProgress={false}
                            placeholder={t("workflowTemplate.search")}
                        />
                    </div>
                    <div className={formStyle.search}>
                        <select
                            id={"product"}
                            value={filterProduct}
                            onChange={(event) => {
                                const product = event.target.value;
                                setFilterProduct(product);
                                setFilterProductType("");
                                if (product !== Profile.BMP && filterTemplateType === TemplateType.INTEGRATION) {
                                    setFilterTemplateType("");
                                }
                            }}
                            className={classNames(formStyle.select, formStyle.fixedWidthInput)}
                            data-testid={testIds.workArea.workflowTemplates.primaryView.filter.productSelect}
                        >
                            <option key={"empty"} value={""}>
                                {t("workflowTemplatesManager.filter.allProducts")}
                            </option>
                            {Object.values(Profile)
                                .filter((profile) => Profile.ALL !== profile)
                                .filter((profile) =>
                                    props.tenantDetails?.featureLicenses.includes(
                                        profileToFeatureLicenseMap.get(profile) as FeatureLicenseType
                                    )
                                )
                                .map((each, index) => (
                                    <option key={index} value={each}>
                                        {profileToNameMap.get(each)}
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div className={formStyle.search}>
                        <select
                            name="version"
                            value={filterProductType}
                            onChange={(e) => setFilterProductType(e.target.value)}
                            className={classNames(form.select, workflowStyle.fixedWidthVersion)}
                            disabled={filterProduct === ""}
                            data-testid={testIds.workArea.workflowTemplates.primaryView.filter.versionSelect}
                        >
                            <option key={"value"} value={""}>
                                {t("workflowTemplatesManager.filter.allVersions")}
                            </option>
                            {optionList}
                        </select>
                    </div>
                    <div className={formStyle.search}>
                        <select
                            name="templateType"
                            value={filterTemplateType}
                            onChange={(e) => setFilterTemplateType(e.target.value)}
                            className={classNames(form.select, workflowStyle.fixedWidthVersion)}
                            data-testid={testIds.workArea.workflowTemplates.primaryView.filter.templateTypeSelect}
                        >
                            <option key={"value"} value={""}>
                                {t("workflowTemplatesManager.filter.allTemplateTypes")}
                            </option>
                            {Object.values(TemplateType).map((each, index) => {
                                if (TemplateType.ALL === each) {
                                    return undefined;
                                }
                                if (
                                    each === TemplateType.INTEGRATION &&
                                    (!assetManagementFlag ||
                                        !userSessionService.userHasAllAuthorities([AUTH_ASSET_INTEGRATION]) ||
                                        (filterProduct !== "" && filterProduct !== Profile.BMP))
                                ) {
                                    return undefined;
                                }
                                return (
                                    <option key={index} value={each}>
                                        {each.replace(/ /g, "-")}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                    <div className={formStyle.search}>
                        <MultiselectDropdown
                            selectName={"useCases"}
                            selectType={"use cases"}
                            filterState={filterUseCases}
                            setFilterState={setFilterUseCases}
                            dropDownList={USE_CASES}
                            testId={testIds.workArea.workflowTemplates.primaryView.filter.useCasesSelect}
                        />
                    </div>
                    <div className={formStyle.search}>
                        <MultiselectDropdown
                            selectName={"processor"}
                            selectType={"processor"}
                            filterState={filterProcessor}
                            setFilterState={setFilterProcessor}
                            dropDownList={PROCESSOR}
                            testId={testIds.workArea.workflowTemplates.primaryView.filter.processorSelect}
                        />
                    </div>
                    <div className={formStyle.search}>
                        <MultiselectDropdown
                            selectName={"coreActivities"}
                            selectType={"core activities"}
                            filterState={filterCoreActivities}
                            setFilterState={setFilterCoreActivities}
                            dropDownList={CORE_ACTIVITIES}
                            testId={testIds.workArea.workflowTemplates.primaryView.filter.coreActivitiesSelect}
                        />
                    </div>
                </div>
            </div>
            <div className={layoutStyle.tableWrapper}>
                {cardState.workflowTemplatesData.length > 0 || initialLoading ? (
                    manifestLoaded ? (
                        <PublishedWorkflowTemplate
                            setCount={props.setCount}
                            data={cardState.workflowTemplatesData}
                            loaded={!initialLoading}
                            workflowEditors={workflowEditors}
                            workflowEditorV3Supported={workflowEditorV3Supported}
                        />
                    ) : (
                        <LoadingIndicator isView />
                    )
                ) : (
                    <div className={workflowStyle.errorMessage}>
                        <EmptyStateIcon />
                        <div className={workflowStyle.errorMessageText}>
                            <TextBlock disableBottomSpacing={false}>
                                {requestFailureMessage !== undefined && requestFailureMessage != ""
                                    ? t("workflowTemplatesManager.template.requestFailed")
                                    : t("workflowTemplatesManager.template.emptyStateMessage")}
                            </TextBlock>
                        </div>
                    </div>
                )}
            </div>
            {manifestLoaded &&
                cardState.cursor != null &&
                cardState.workflowTemplatesData.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttonStyle.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false, new AbortController());
                            usageStatisticsService.sendEvent({
                                label: "workflows",
                                action: Action.LOAD_MORE,
                                category: Category.WORKFLOW,
                            });
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
        </>
    );
};

export default connector(WorkflowTemplateCardView);
